<template>
  <div
    class="chat-settings bg-gray-50 dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 p-4 overflow-y-auto"
  >
    <div class="space-y-4">
      <!-- Header -->
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">Settings</h3>
        <Button variant="ghost" size="sm" @click="resetToDefaults" title="Reset to defaults">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </Button>
      </div>

      <!-- Temperature -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <label class="text-xs font-medium text-gray-700 dark:text-gray-300"> Temperature </label>
          <span class="text-xs text-gray-500 dark:text-gray-400">{{ settings.temperature }}</span>
        </div>
        <input
          type="range"
          :value="settings.temperature"
          @input="updateTemperature"
          min="0"
          max="2"
          step="0.1"
          class="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
        />
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Controls randomness: 0 is focused, 2 is creative
        </p>
      </div>

      <!-- Max Tokens -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <label class="text-xs font-medium text-gray-700 dark:text-gray-300"> Max Tokens </label>
          <span class="text-xs text-gray-500 dark:text-gray-400">
            {{ settings.maxTokens ?? 'Unlimited' }}
          </span>
        </div>
        <input
          type="number"
          :value="settings.maxTokens ?? ''"
          @input="updateMaxTokens"
          placeholder="Unlimited"
          min="1"
          max="32000"
          class="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Maximum tokens to generate (leave empty for unlimited)
        </p>
      </div>

      <!-- Top P -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <label class="text-xs font-medium text-gray-700 dark:text-gray-300"> Top P </label>
          <span class="text-xs text-gray-500 dark:text-gray-400">{{ settings.topP }}</span>
        </div>
        <input
          type="range"
          :value="settings.topP"
          @input="updateTopP"
          min="0"
          max="1"
          step="0.05"
          class="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
        />
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Nucleus sampling: consider tokens with top_p probability mass
        </p>
      </div>

      <!-- Frequency Penalty -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <label class="text-xs font-medium text-gray-700 dark:text-gray-300">
            Frequency Penalty
          </label>
          <span class="text-xs text-gray-500 dark:text-gray-400">{{
            settings.frequencyPenalty
          }}</span>
        </div>
        <input
          type="range"
          :value="settings.frequencyPenalty"
          @input="updateFrequencyPenalty"
          min="-2"
          max="2"
          step="0.1"
          class="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
        />
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Penalize tokens based on frequency in the text
        </p>
      </div>

      <!-- Presence Penalty -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <label class="text-xs font-medium text-gray-700 dark:text-gray-300">
            Presence Penalty
          </label>
          <span class="text-xs text-gray-500 dark:text-gray-400">{{
            settings.presencePenalty
          }}</span>
        </div>
        <input
          type="range"
          :value="settings.presencePenalty"
          @input="updatePresencePenalty"
          min="-2"
          max="2"
          step="0.1"
          class="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
        />
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Penalize tokens based on presence in the text
        </p>
      </div>

      <!-- Stream Toggle -->
      <div
        class="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700"
      >
        <div>
          <label class="text-xs font-medium text-gray-700 dark:text-gray-300">
            Stream Response
          </label>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Show tokens as they're generated
          </p>
        </div>
        <Switch
          :model-value="settings.stream"
          @update:model-value="value => emit('update:settings', { ...props.settings, stream: value })"
        />
      </div>

      <!-- Seed -->
      <div class="pt-2 border-t border-gray-200 dark:border-gray-700">
        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Seed</label>
        <input
          type="number"
          :value="settings.seed ?? ''"
          @input="updateSeed"
          placeholder="random"
          class="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
        />
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Fixed integer makes output reproducible.
        </p>
      </div>

      <!-- Stop sequences -->
      <div>
        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Stop sequences
        </label>
        <input
          type="text"
          :value="settings.stop.join(', ')"
          @input="updateStop"
          placeholder="e.g. \nEND, [[STOP]]"
          class="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
        />
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Comma-separated. Model halts when any appears.
        </p>
      </div>

      <!-- Reasoning effort (OpenAI reasoning models) -->
      <div v-if="modelSupportsReasoning">
        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Reasoning effort
        </label>
        <select
          :value="settings.reasoningEffort ?? ''"
          @change="updateReasoningEffort"
          class="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
        >
          <option value="">auto</option>
          <option value="low">low</option>
          <option value="medium">medium</option>
          <option value="high">high</option>
        </select>
      </div>

      <!-- Thinking budget (Gemini 2.5 / 3.x) -->
      <div v-if="modelSupportsThinking">
        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Thinking budget (tokens)
        </label>
        <input
          type="number"
          :value="settings.thinkingBudget ?? ''"
          @input="updateThinkingBudget"
          placeholder="auto"
          min="0"
          class="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
        />
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          0 disables thinking. Leave blank for model default.
        </p>
      </div>

      <!-- Response format -->
      <div class="pt-2 border-t border-gray-200 dark:border-gray-700">
        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Response format
        </label>
        <select
          :value="settings.responseFormat"
          @change="updateResponseFormat"
          class="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
        >
          <option value="text">text</option>
          <option value="json_object">json_object</option>
          <option value="json_schema">json_schema</option>
        </select>
        <textarea
          v-if="settings.responseFormat === 'json_schema'"
          :value="settings.jsonSchema"
          @input="updateJsonSchema"
          rows="6"
          placeholder='{"name":"my_schema","schema":{"type":"object","properties":{...}}}'
          class="mt-2 w-full px-2 py-1 text-xs font-mono border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
        />
      </div>

      <!-- Tools editor -->
      <div>
        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Tools <span class="text-gray-400">(JSON array)</span>
        </label>
        <textarea
          :value="settings.tools"
          @input="updateTools"
          rows="6"
          placeholder='[{"type":"function","function":{"name":"...","parameters":{...}}}]'
          class="w-full px-2 py-1 text-xs font-mono border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
        />
        <p v-if="toolsError" class="text-xs text-red-500 mt-1">{{ toolsError }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { Button, Switch } from '@asabdev/asab-ui';
  import type { ChatCompletionSettings } from '../../types/session';
  import type { Model } from '../../types/models';

  interface Props {
    settings: ChatCompletionSettings;
    model?: Model | null;
  }

  const props = defineProps<Props>();

  interface Emits {
    (e: 'update:settings', settings: ChatCompletionSettings): void;
  }

  const emit = defineEmits<Emits>();

  const modelSupportsReasoning = computed(
    () => props.model?.metadata?.supports_reasoning_effort === true
  );
  const modelSupportsThinking = computed(
    () => props.model?.metadata?.supports_thinking_budget === true
  );

  const updateTemperature = (event: Event) => {
    const value = parseFloat((event.target as HTMLInputElement).value);
    emit('update:settings', { ...props.settings, temperature: value });
  };

  const updateMaxTokens = (event: Event) => {
    const value = (event.target as HTMLInputElement).value;
    emit('update:settings', {
      ...props.settings,
      maxTokens: value ? parseInt(value) : null,
    });
  };

  const updateTopP = (event: Event) => {
    const value = parseFloat((event.target as HTMLInputElement).value);
    emit('update:settings', { ...props.settings, topP: value });
  };

  const updateFrequencyPenalty = (event: Event) => {
    const value = parseFloat((event.target as HTMLInputElement).value);
    emit('update:settings', { ...props.settings, frequencyPenalty: value });
  };

  const updatePresencePenalty = (event: Event) => {
    const value = parseFloat((event.target as HTMLInputElement).value);
    emit('update:settings', { ...props.settings, presencePenalty: value });
  };

  const updateSeed = (event: Event) => {
    const raw = (event.target as HTMLInputElement).value;
    emit('update:settings', {
      ...props.settings,
      seed: raw === '' ? null : parseInt(raw, 10),
    });
  };

  const updateStop = (event: Event) => {
    const raw = (event.target as HTMLInputElement).value;
    const stop = raw
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    emit('update:settings', { ...props.settings, stop });
  };

  const updateReasoningEffort = (event: Event) => {
    const v = (event.target as HTMLSelectElement).value as '' | 'low' | 'medium' | 'high';
    emit('update:settings', {
      ...props.settings,
      reasoningEffort: v === '' ? null : v,
    });
  };

  const updateThinkingBudget = (event: Event) => {
    const raw = (event.target as HTMLInputElement).value;
    emit('update:settings', {
      ...props.settings,
      thinkingBudget: raw === '' ? null : parseInt(raw, 10),
    });
  };

  const updateResponseFormat = (event: Event) => {
    const v = (event.target as HTMLSelectElement).value as
      | 'text'
      | 'json_object'
      | 'json_schema';
    emit('update:settings', { ...props.settings, responseFormat: v });
  };

  const updateJsonSchema = (event: Event) => {
    const raw = (event.target as HTMLTextAreaElement).value;
    emit('update:settings', { ...props.settings, jsonSchema: raw });
  };

  const toolsError = ref<string | null>(null);
  const updateTools = (event: Event) => {
    const raw = (event.target as HTMLTextAreaElement).value;
    toolsError.value = null;
    if (raw.trim()) {
      try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) throw new Error('tools must be a JSON array');
      } catch (e) {
        toolsError.value = (e as Error).message;
      }
    }
    emit('update:settings', { ...props.settings, tools: raw });
  };

  const resetToDefaults = () => {
    emit('update:settings', {
      temperature: 0.7,
      maxTokens: 200,
      topP: 1,
      frequencyPenalty: 0,
      presencePenalty: 0,
      stream: true,
      seed: null,
      stop: [],
      responseFormat: 'text',
      jsonSchema: '',
      tools: '',
      reasoningEffort: null,
      thinkingBudget: null,
    });
  };
</script>

<style scoped>
  .chat-settings {
    width: 280px;
    max-height: calc(100vh - 300px);
  }
</style>
